import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Inbox } from 'lucide-react';
import { getCategoryBySlug, getErrorCode } from '@/services/api';
import { Breadcrumbs, SectionRow } from '@/components';
import { ErrorState, EmptyState, LoadingSpinner } from '@airclou/ui';

export function CategoryPage() {
  const { slug, categorySlug } = useParams<{ slug?: string; categorySlug?: string }>();
  const navigate = useNavigate();

  // Support both old (/categories/:slug) and new (/:categorySlug) formats
  const effectiveSlug = categorySlug || slug;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['category', effectiveSlug],
    queryFn: () => getCategoryBySlug(effectiveSlug!),
    enabled: !!effectiveSlug,
  });

  // Handle 301 redirect from old slug to new slug
  useEffect(() => {
    if (data?.redirect) {
      navigate(`/${data.redirect.newSlug}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    const errorCode = getErrorCode(error);
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState
          description={
            errorCode === 'NOT_FOUND'
              ? 'This category could not be found.'
              : 'Failed to load category. Please try again.'
          }
          action={
            errorCode !== 'NOT_FOUND' && (
              <button
                onClick={() => refetch()}
                className="text-primary hover:underline"
              >
                Try again
              </button>
            )
          }
        />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { category, sections = [] } = data;

  if (!category) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState description="This category could not be found." />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: category.name },
        ]}
      />

      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          {category.name}
        </h1>
        {category.description && (
          <p className="mt-2 text-muted-foreground">{category.description}</p>
        )}
      </div>

      {sections.length > 0 ? (
        <div className="space-y-3">
          {sections.map((section) => (
            <SectionRow key={section.id} section={{ ...section, category }} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Inbox}
          title="No sections yet"
          description="This category doesn't have any sections yet."
        />
      )}
    </div>
  );
}
